import avatarLogoBlack from "@/assets/img/avatar-black.svg";
import brandLogoBlack from "@/assets/img/brand-logo-black.svg";
import docsLinkArrowBlack from "@/assets/img/graphapi-docs.svg";
import homeButtonBlack from "@/assets/img/home.svg";
import {
  BCollapse,
  BDropdownItem,
  BNavbar,
  BNavbarBrand,
  BNavbarNav,
} from "@/lib/typed-bootstrap";
import DropdownsState, {
  DropdownStateEnum,
} from "@/mixins/DropdownsState.mixin";
import { getOrgNames } from "@/models/org";
import { RouteAuthRequirementEnum } from "@/router/types";
import { INavSection, NavModule } from "@/store/modules/nav";
import { OrgModule } from "@/store/modules/org";
import { UserModule } from "@/store/modules/user";
import { envRootDomain, envRootLocalizedDomain } from "@/utils";
import { userDataLoader } from "@/utils/auth-service";
import { persistanceService } from "@/utils/persistance-service";
import { Component, Prop, Watch } from "vue-property-decorator";
import * as tsx from "vue-tsx-support";
import Link from "../shared/Link.component";
import styles from "./Navbar.component.module.scss";
import NavbarDropdownDesktop from "./NavbarDropdownDesktop.component";

@Component
export default class Navbar extends DropdownsState {
  _tsx!: tsx.DeclareProps<{
    showSecondaryNav?: Navbar["showSecondaryNav"];
  }>;

  @Prop({ default: false }) showSecondaryNav: boolean;

  $scopedSlots!: tsx.InnerScopedSlots<{
    secondaryNavbar?: {
      onLinkClick: () => void;
      toggleMobileDropdown?: () => void;
    };
    publishButton?: {};
    navigationLeft?: {};
    navigationLeftDesktop?: {};
  }>;

  isLoading = false;
  mobileToggleId = "mobileNavDropdown";
  isSmallScreen = false;
  userOrgs: { id: string; name: string }[] = [];

  avatarSrc = avatarLogoBlack;
  brandLogoSrc = brandLogoBlack;
  docsSrc = docsLinkArrowBlack;
  homeSrc = homeButtonBlack;

  get isMobileDropdownShown() {
    return this.dropDownState[this.mobileToggleId] === DropdownStateEnum.shown;
  }

  get docsUrl() {
    return `${envRootDomain()}/docs`;
  }

  get homepageUrl() {
    return envRootLocalizedDomain();
  }

  get orgId(): string {
    return OrgModule.id;
  }

  get orgName(): string {
    return OrgModule.name ?? "Unknown organization";
  }

  get userEmail(): string {
    return UserModule.email;
  }

  get isAuthOnlyRoute() {
    return (
      this.$route.name &&
      (!this.$route.meta?.authMode ||
        this.$route.meta.authMode === RouteAuthRequirementEnum.authOnly)
    );
  }

  get hideLoginButton(): boolean {
    const name = this.$route.name || "";
    return (
      name.trim().toLowerCase() !== "signup" &&
      name.trim().toLowerCase() !== "password-reset"
    );
  }

  get hideSignupButton(): boolean {
    const name = this.$route.name || "";
    return name.trim().toLowerCase() !== "login";
  }

  get showHomeButton(): boolean {
    return this.$route.params.orgId === undefined && this.userIsAuthenticated;
  }

  get userIsAuthenticated(): boolean {
    return UserModule.isAuthenticated;
  }

  get navItems(): INavSection[] {
    return NavModule.apiDetailsNavItems;
  }

  get showApiNavItems(): boolean {
    return this.$route.params.apiId !== undefined;
  }

  get showWorkspaceItems(): boolean {
    return this.$route.params.orgId !== undefined && this.userOrgs.length > 0;
  }

  get apiId(): string {
    return this.$route.params.apiId;
  }

  get apiName(): string {
    return NavModule.currentApiName;
  }

  get activeNavItem(): string {
    return this.$route.meta?.localizedName ?? "";
  }

  get hasSecondaryNavbar(): boolean {
    return this.$scopedSlots.secondaryNavbar !== undefined;
  }

  get userOrgIds() {
    return Object.keys(UserModule.roles);
  }

  logout() {
    this.$authManager.logout();
  }

  @Watch("userIsAuthenticated")
  async onUserAuthChange(): Promise<void> {
    if (!this.userIsAuthenticated) {
      this.hideDropdown(this.mobileToggleId);
    }
  }

  async onLinkClick(): Promise<void> {
    try {
      this.hideDropdown(this.mobileToggleId);
    } catch (err) {
      console.log(err);
    }
  }

  @Watch("userOrgIds", { immediate: true })
  async getOrgNames() {
    if (this.userOrgIds.length > 0) {
      this.userOrgs = await getOrgNames(this.userOrgIds);
    } else {
      this.userOrgs = [];
    }
  }

  async onSelectOrg(orgId: string) {
    await UserModule.onUserAction(async () => {
      userDataLoader.switchOrg();
    });
    this.$router
      .push({
        name: "org-api-dashboard",
        params: {
          orgId,
        },
      })
      .catch(() => undefined)
      .finally(() => this.hideDropdown(this.mobileToggleId));
  }

  toggleMobileDropdown(): void {
    this.toggleDropdown(this.mobileToggleId);
  }

  onLogoClick() {
    persistanceService.removeActiveRouteKey();
    this.$router.push({ name: "home" }).catch(() => undefined);
  }

  render() {
    return (
      <div>
        {/* PRIMARY NAVBAR */}
        <BNavbar class={styles.primaryNav}>
          <BNavbarBrand
            class={{
              [styles.brandAuthenticated]: this.userIsAuthenticated,
            }}
          >
            {this.showHomeButton ? (
              <button
                class={styles.brandAndHomeButton}
                onClick={this.onLogoClick}
              >
                <img src={this.homeSrc} />
              </button>
            ) : this.userIsAuthenticated ? (
              <Link
                class={styles.homepageUrl}
                to={{ name: "org-api-dashboard" }}
              >
                <img src={this.brandLogoSrc} />
              </Link>
            ) : (
              <a href={this.homepageUrl}>
                <img src={this.brandLogoSrc} />
              </a>
            )}
            {this.$scopedSlots.navigationLeft?.({})}
            <div class={styles.desktop}>
              {this.$scopedSlots.navigationLeftDesktop?.({})}
            </div>
          </BNavbarBrand>

          {/* AUTHENTICATED */}
          {this.isAuthOnlyRoute ? (
            <div class="ml-auto">
              {/* DESKTOP */}
              <BNavbarNav>
                <a
                  href={this.docsUrl}
                  class={styles.docsButton}
                  target="_blank"
                >
                  {this.$t("nav_docs")}
                  <img
                    alt={this.$t("nav_docs").toString()}
                    src={this.docsSrc}
                  />
                </a>
                <div class={styles.desktop}>
                  <NavbarDropdownDesktop />
                </div>
              </BNavbarNav>

              {/* MOBILE */}
              <button
                class={{
                  "is-active": this.isMobileDropdownShown,
                  [styles.hamburger]: true,
                  "hamburger--elastic": true,
                  [styles.activeHamburger]: true,
                }}
                aria-expanded={this.isMobileDropdownShown}
                type="button"
                aria-label="Menu"
                aria-controls="navigation"
                onClick={this.toggleMobileDropdown}
              >
                <span class="hamburger-box">
                  <span class="hamburger-inner"></span>
                </span>
              </button>
            </div>
          ) : null}
        </BNavbar>

        {/* SECONDARY NAVBAR */}
        {this.isAuthOnlyRoute && this.$scopedSlots.secondaryNavbar && (
          <BNavbar class={styles.secondaryNav}>
            {this.$scopedSlots.secondaryNavbar({
              onLinkClick: this.onLinkClick,
              toggleMobileDropdown: this.toggleMobileDropdown,
            })}
          </BNavbar>
        )}

        {/* MOBILE COLLAPSED NAVBAR */}
        <div class={styles.mobile}>
          {this.isAuthOnlyRoute && (
            <BCollapse isNav visible={this.isMobileDropdownShown}>
              <BNavbar
                class={{
                  [styles.hamburgerMobile]: true,
                  [styles.opened]: this.isMobileDropdownShown,
                }}
              >
                <li class={styles.navItem}>
                  <div class={styles.user}>
                    <img src={this.avatarSrc} class="avatar" />
                    <div class={styles.userEmail}>
                      {this.userEmail}
                      <span class={styles.userOrg}>{this.orgName}</span>
                    </div>
                  </div>
                </li>

                {this.showWorkspaceItems && (
                  <div>
                    <li class={styles.line}></li>
                    <li class={styles.navSection}>
                      {this.$t("nav_dropdown_workspaces_header")}
                    </li>
                    {this.userOrgs.map((org) => (
                      <BDropdownItem
                        key={org.id}
                        class={styles.dropdownItem}
                        data-testid={`org-dropdown-${org.id}`}
                        onClick={() => this.onSelectOrg(org.id)}
                      >
                        {org.name}
                      </BDropdownItem>
                    ))}
                  </div>
                )}

                {this.showApiNavItems &&
                  this.navItems.map((section) => (
                    <div key={section.name}>
                      <li class={styles.line}></li>
                      <li class={styles.navSection}>{section.name}</li>
                      {section.items.map((item) => (
                        <router-link
                          key={item.text}
                          custom
                          to={{
                            name: item.link.name,
                            params: { apiId: this.apiId },
                          }}
                          scopedSlots={{
                            default: ({ href, navigate, isActive }: any) => (
                              <li
                                class={{
                                  [styles.navItem]: true,
                                  [styles.navItemActive]: isActive,
                                }}
                              >
                                <a
                                  href={href}
                                  onClick={(e) => {
                                    navigate(e);
                                    this.onLinkClick();
                                  }}
                                >
                                  {item.text}
                                </a>
                              </li>
                            ),
                          }}
                        />
                      ))}
                    </div>
                  ))}

                <li class={styles.line}></li>
                <li class={styles.navItem}>
                  <a href="#" onClick={this.logout}>
                    {this.$t("logout_cta_submit")}
                  </a>
                </li>
              </BNavbar>
            </BCollapse>
          )}
          {this.$scopedSlots.publishButton?.({})}
        </div>
      </div>
    );
  }
}
